import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from "@angular/router";
import { blankWarrantyRecord, Company, CompanyConstructionMap, IWarrantyRecord, MessageType, TOKEN_URL_PARAM_KEY, WarrantyResponse } from '@models';
import { caseCompany, isValidCompany, matchesWarrantyRouteIds, normalizeClaimId, resolveCompanyConstruction } from '@utilities';
import { ApplicationService, DataService } from "@warranty/services";
import { catchError, finalize, map, Observable, of } from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class WarrantyResolver implements Resolve<any> {
  private readonly applicationService = inject(ApplicationService);
  private readonly dataService = inject(DataService);
  private readonly router = inject(Router);

  resolve(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<IWarrantyRecord> {
    // Handle create registration: 'warranty/:company/:constructionType'
    let company: string = `${route.params['company'] ?? ''}`.trim();
    let constructionType: string = `${route.params['constructionType'] ?? ''}`.trim();

    const createRegistration = this.resolveCreateWarranty(company, constructionType);
    if (createRegistration) { return createRegistration; }

    // Handle create claim: 'warranty/claim/:registrationId(company)/:claimId(constructionType)'
    company = `${route.params['registrationId'] ?? ''}`.trim();
    constructionType = `${route.params['claimId'] ?? ''}`.trim();

    const createClaim = this.resolveCreateWarranty(company, constructionType);
    if (createClaim) { return createClaim; }

    // Guard that anything reaching this point is the expected warranty flow
    const registrationId: string = `${route.params['registrationId'] ?? ''}`.trim();
    if (!registrationId) return of({ ...blankWarrantyRecord });
    const claimId = normalizeClaimId(route.params['claimId']);

    // Try to pull token from URL query params first
    const rawToken = route.queryParams?.[TOKEN_URL_PARAM_KEY];
    const urlToken = typeof rawToken === 'string' ? rawToken.trim() : null;

    // Prioritize urlToken over storedTokens.token
    const token: string | null = urlToken || this.applicationService.getWarrantyTokens().token || null;
    // Don't update the applicationService.warrantyTokens here because the warranty-link.component handles that
    if (!token) {
      return this.resolveWarrantyWithoutToken(registrationId, claimId);
    }

    this.applicationService.busy.set(1);
    return this.dataService.warrantyGetByToken$(token).pipe(
      map((data) => {
        const warranty = data?.warranty;
        if (warranty?.id) {
          // Check that the url's registrationId and optionally claimId match the warranty from the token
          if (!matchesWarrantyRouteIds(warranty, registrationId, claimId)) {
            this.applicationService.clearWarrantyTokens();
            throw new Error('Warranty token does not match route ids');
          }
          this.setWarrantyContext(warranty);
          return warranty;
        }
        throw new Error('Warranty token did not return a warranty');
      }),
      catchError(() => {
        this.applicationService.clearWarrantyTokens();
        return this.resolveWarrantyWithoutToken(registrationId, claimId);
      }),
      finalize(() => {
        this.applicationService.busy.set(0);
      })
    );
  }

  // Checks if the user is a logged in admin to resolve the warranty without the token
  private resolveWarrantyWithoutToken(registrationId: string, claimId?: string): Observable<IWarrantyRecord> {
    if (this.applicationService.isAdmin()) {
      return this.resolveAdminWarranty(registrationId, claimId);
    }
    this.redirectToLegacy(registrationId, claimId);
    this.applicationService.busy.set(0);
    return of({ ...blankWarrantyRecord });
  }

  private resolveAdminWarranty(registrationId: string, claimId?: string): Observable<IWarrantyRecord> {
    this.applicationService.busy.set(1);
    return this.dataService.warrantyGetByAdmin$(registrationId, claimId).pipe(
      map((data: WarrantyResponse) => {
        const warranty: IWarrantyRecord = data?.warranty;
        if (!warranty?.id || !matchesWarrantyRouteIds(warranty, registrationId, claimId)) {
          this.applicationService.clearWarrantyTokens();
          void this.applicationService.redirectToFindClaim();
          return { ...blankWarrantyRecord };
        }

        this.applicationService.replaceWarrantyTokens(data.tokens?.token);
        this.setWarrantyContext(warranty);
        return warranty;
      }),
      catchError(() => {
        this.applicationService.clearWarrantyTokens();
        void this.applicationService.redirectToFindClaim();
        return of({ ...blankWarrantyRecord });
      }),
      finalize(() => {
        this.applicationService.busy.set(0);
      }),
    );
  }

  private setWarrantyContext(warranty: IWarrantyRecord) {
    const company = caseCompany(warranty.company);
    const construction = resolveCompanyConstruction(company, warranty.batType, true);
    warranty.company = company;
    warranty.batType = construction;
    this.applicationService.company.set(company);
    this.applicationService.constructionType.set(construction);
  }

  private resolveCreateWarranty(company: string, constructionType: string): Observable<IWarrantyRecord> | null {
    if (!constructionType || !isValidCompany(company)) { return null; }

    const resolvedCompany = caseCompany(company);
    const construction = resolveCompanyConstruction(resolvedCompany, constructionType);
    if (!construction) {
      this.redirectInvalidConstruction(resolvedCompany, constructionType);
      return of({ ...blankWarrantyRecord });
    }

    return of({
      ...blankWarrantyRecord,
      company: resolvedCompany,
      batType: construction
    });
  }

  private redirectInvalidConstruction(company: Company, constructionType: string) {
    this.applicationService.addMessage(
      `"${constructionType}" is not a valid construction type for ${company}. Valid construction types: ${CompanyConstructionMap[company].join(', ')}.`,
      MessageType.ERROR
    );
    void this.router.navigate(['/home'], { replaceUrl: true });
  }

  private redirectToLegacy(registrationId: string, claimId?: string) {
    const path = ['/warranty', 'legacy', registrationId];
    if (claimId) {
      path.push(claimId);
    }
    void this.router.navigate(path, { replaceUrl: true });
  }
}
